import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import {
  getCustomerById,
  getCustomerSessionsByCompanyId,
  getContactPersonsByCompanyId,
  logAccess,
} from '../firebase/firestore';
import {
  CustomerData,
  CustomerSession,
  CalculatedResult,
  ContactPerson,
} from '../firebase/types';
import { DashboardData, CalculatedData } from '../types';
import Charts from '../components/Charts';
import MetricsCards from '../components/MetricsCards';
import SettingsPanel from '../components/SettingsPanel';
import CompanyStatusIcon from '../components/CompanyStatusIcon';

const AdminDashboardDetailPage: React.FC = () => {
  const { user, adminUser } = useAuth();
  const [companyId, setCompanyId] = useState<string>('');
  const [companyData, setCompanyData] = useState<CustomerData | null>(null);
  const [sessions, setSessions] = useState<CustomerSession[]>([]);
  const [contactPersons, setContactPersons] = useState<ContactPerson[]>([]);
  const [selectedContactPersonId, setSelectedContactPersonId] = useState<
    string | null
  >(null);
  const [dataType, setDataType] = useState<'first' | 'latest'>('first');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // URLからcompanyIdを取得
  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];
    if (id) {
      setCompanyId(id);
    }
  }, []);

  // 企業データとセッションデータを取得
  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return;

      try {
        setIsLoading(true);
        setError('');

        // 企業データを取得
        const customer = await getCustomerById(companyId);
        if (!customer) {
          setError('企業データが見つかりませんでした');
          setIsLoading(false);
          return;
        }
        setCompanyData(customer.customerData);

        // セッションデータを取得
        const sessionData = await getCustomerSessionsByCompanyId(companyId);
        setSessions(sessionData);

        // 担当者データを取得
        const contactPersonsData =
          await getContactPersonsByCompanyId(companyId);
        setContactPersons(contactPersonsData);

        // 最初の担当者を選択（セッションがある場合）
        if (contactPersonsData.length > 0 && sessionData.length > 0) {
          // 最新のセッションの担当者IDを取得
          const latestContactPersonId = sessionData[0].contactPersonId;
          if (contactPersonsData.some(cp => cp.id === latestContactPersonId)) {
            setSelectedContactPersonId(latestContactPersonId);
          } else {
            // 最新セッションの担当者が見つからない場合、最初の担当者を選択
            setSelectedContactPersonId(contactPersonsData[0].id || null);
          }
        } else if (contactPersonsData.length > 0) {
          setSelectedContactPersonId(contactPersonsData[0].id || null);
        }

        // アクセスログを記録
        if (user?.uid && adminUser) {
          await logAccess(user.uid, companyId, 'view_dashboard');
        }
      } catch (err) {
        setError('データの取得に失敗しました');
        // eslint-disable-next-line no-console
        console.error('Error loading dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [companyId, user, adminUser]);

  // 選択された担当者のセッションを取得
  const selectedContactPersonSessions = useMemo(() => {
    if (!selectedContactPersonId) return [];
    return sessions.filter(s => s.contactPersonId === selectedContactPersonId);
  }, [sessions, selectedContactPersonId]);

  // 選択された担当者情報
  const selectedContactPerson = useMemo(() => {
    if (!selectedContactPersonId) return null;
    return contactPersons.find(cp => cp.id === selectedContactPersonId) || null;
  }, [contactPersons, selectedContactPersonId]);

  // 担当期間の型定義
  interface AssignmentPeriod {
    id: string;
    contactPersonId: string;
    contactPersonName: string;
    contactPersonPosition: string;
    startTime: Date;
    endTime: Date | null; // nullの場合は最後の担当者（現在まで）
    isCurrent: boolean; // 選択中の担当者の期間かどうか
  }

  // 担当者履歴（担当期間単位）: セッションから構築
  const contactPersonHistory = useMemo(() => {
    if (sessions.length === 0) return [];

    // セッションをsavedAtで時系列ソート（昇順：古い順）
    const sortedSessions = [...sessions].sort(
      (a, b) => a.savedAt.getTime() - b.savedAt.getTime()
    );

    const periods: AssignmentPeriod[] = [];
    let currentPeriodStart: Date | null = null;
    let currentContactPersonId: string | null = null;

    // 時系列で順に走査し、contactPersonIdが変わる時点を検出
    for (let i = 0; i < sortedSessions.length; i++) {
      const session = sortedSessions[i];

      // 担当者が変わった時点で、前の担当期間を終了
      if (
        currentContactPersonId !== null &&
        currentContactPersonId !== session.contactPersonId &&
        currentPeriodStart !== null
      ) {
        // 前の担当期間の終了日時は、現在の担当期間の開始日時
        // currentContactPersonIdをコピーしてループ内の関数で安全に使用
        const periodToClose = {
          contactPersonId: currentContactPersonId,
          startTime: currentPeriodStart,
        };
        const prevContactPerson = contactPersons.find(
          cp => cp.id === periodToClose.contactPersonId
        );
        if (prevContactPerson) {
          periods.push({
            id: `period-${periodToClose.contactPersonId}-${periodToClose.startTime.getTime()}`,
            contactPersonId: periodToClose.contactPersonId,
            contactPersonName: prevContactPerson.name,
            contactPersonPosition: prevContactPerson.position,
            startTime: periodToClose.startTime,
            endTime: session.savedAt, // 次の担当者の開始日時
            isCurrent:
              selectedContactPersonId === periodToClose.contactPersonId,
          });
        }
        // 新しい担当期間を開始
        currentPeriodStart = session.savedAt;
        currentContactPersonId = session.contactPersonId;
      } else if (currentContactPersonId === null) {
        // 最初のセッション
        currentPeriodStart = session.savedAt;
        currentContactPersonId = session.contactPersonId;
      }
      // 同じ担当者が続いている場合は何もしない（期間を継続）
    }

    // 最後の担当期間を追加
    if (currentContactPersonId !== null && currentPeriodStart !== null) {
      const finalPeriod = {
        contactPersonId: currentContactPersonId,
        startTime: currentPeriodStart,
      };
      const lastContactPerson = contactPersons.find(
        cp => cp.id === finalPeriod.contactPersonId
      );
      if (lastContactPerson) {
        periods.push({
          id: `period-${finalPeriod.contactPersonId}-${finalPeriod.startTime.getTime()}`,
          contactPersonId: finalPeriod.contactPersonId,
          contactPersonName: lastContactPerson.name,
          contactPersonPosition: lastContactPerson.position,
          startTime: finalPeriod.startTime,
          endTime: null, // 最後の担当者なので終了日時なし
          isCurrent: selectedContactPersonId === finalPeriod.contactPersonId,
        });
      }
    }

    // 開始日時の降順でソート（最新の担当期間が先頭）
    return periods.sort(
      (a, b) => b.startTime.getTime() - a.startTime.getTime()
    );
  }, [sessions, contactPersons, selectedContactPersonId]);

  // 表示するデータを決定
  const displayData = useMemo((): {
    customerData: CustomerData;
    calculatedResult: CalculatedResult;
  } | null => {
    if (selectedContactPersonSessions.length === 0 || !companyData) return null;

    if (dataType === 'first') {
      // 最初の保存（ボタン押下後）: 最も古いセッション（savedAtが最も古い）
      // selectedContactPersonSessionsはsavedAtで降順（desc）にソートされているため、最後の要素が最も古い
      const firstSession =
        selectedContactPersonSessions[selectedContactPersonSessions.length - 1];
      return {
        customerData: firstSession.customerData,
        calculatedResult: firstSession.calculatedResult,
      };
    } else {
      // 最新の保存（最終保存データ）: 最新のセッション（savedAtが最も新しい）
      const latestSession = selectedContactPersonSessions[0];
      return {
        customerData: latestSession.customerData,
        calculatedResult: latestSession.calculatedResult,
      };
    }
  }, [selectedContactPersonSessions, dataType, companyData]);

  // DashboardDataに変換
  const dashboardData: DashboardData | null = displayData
    ? {
        companyName: displayData.customerData.companyName,
        generalHiring: displayData.customerData.generalHiring,
        techHiring: displayData.customerData.techHiring,
        budget: displayData.customerData.budget,
        companySize: displayData.customerData.companySize,
        industry: displayData.customerData.industry,
        salary: displayData.customerData.salary,
        stability: displayData.customerData.stability,
        growth: displayData.customerData.growth,
        organicRatio: displayData.customerData.organicRatio,
        snsFollowers: displayData.customerData.snsFollowers,
        pagePV: displayData.customerData.pagePV,
        rating: displayData.customerData.rating,
        brandVisibility: displayData.customerData.brandVisibility,
        effectiveJobSeekingRate:
          displayData.customerData.effectiveJobSeekingRate,
        prMeasures: displayData.customerData.prMeasures,
        regionalFactor: displayData.customerData.regionalFactor,
        workLifeBalance: displayData.customerData.workLifeBalance,
        technicalCapability: displayData.customerData.technicalCapability,
      }
    : null;

  // CalculatedDataに変換
  const calculatedData: CalculatedData | null = displayData
    ? {
        finalRatio: displayData.calculatedResult.finalRatio,
        difficulty: displayData.calculatedResult.difficulty,
        difficultyClass: displayData.calculatedResult.difficultyClass,
        requiredApplicants: displayData.calculatedResult.requiredApplicants,
        costPerHire: displayData.calculatedResult.costPerHire,
        brandVisibility: displayData.customerData.brandVisibility,
        effectiveJobSeekingRate:
          displayData.customerData.effectiveJobSeekingRate,
      }
    : null;

  const handleBack = () => {
    window.location.href = '/admin/companies';
  };

  // 管理画面では設定パネルは読み取り専用
  const handleInputChange = () => {
    // 管理画面では変更不可
  };

  const handleCheckboxChange = () => {
    // 管理画面では変更不可
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600">読み込み中...</p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (error || !dashboardData || !calculatedData) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center space-y-4">
            <p className="text-red-600">
              {error || 'データが見つかりませんでした'}
            </p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              戻る
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen">
        {/* ヘッダー */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">
                      <div className="flex items-center space-x-2">
                        <span>{companyData?.companyName || '企業名不明'}</span>
                        {companyData && (
                          <CompanyStatusIcon
                            status={companyData.status}
                            size="sm"
                          />
                        )}
                      </div>
                    </h1>
                    <p className="text-sm text-gray-600">
                      {companyData?.corporateNumber || '法人番号未登録'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  採用戦略分析ダッシュボード 2025
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* メインコンテンツ */}
        <main>
          {/* 担当者系セクション（紫色背景 - 端から端まで） */}
          <div className="py-6 bg-purple-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-4">
                {/* 担当者選択 */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <svg
                      className="h-5 w-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    <h2 className="text-lg font-semibold text-gray-900">
                      担当者選択
                    </h2>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      担当者を選択:
                    </label>
                    <select
                      value={selectedContactPersonId || ''}
                      onChange={e =>
                        setSelectedContactPersonId(e.target.value || null)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {contactPersons.length === 0 ? (
                        <option value="">担当者が見つかりません</option>
                      ) : (
                        contactPersons.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name} ({person.position})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* 担当者履歴（時系列） */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <svg
                      className="h-5 w-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h2 className="text-lg font-semibold text-gray-900">
                      担当者履歴（時系列）
                    </h2>
                  </div>
                  <div
                    className={`space-y-2 border border-gray-200 rounded-md p-2 bg-gray-50 ${
                      contactPersonHistory.length > 1
                        ? 'max-h-48 overflow-y-auto'
                        : ''
                    }`}
                  >
                    {contactPersonHistory.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-2">
                        履歴がありません
                      </p>
                    ) : (
                      contactPersonHistory.map(period => {
                        const formatDateTime = (date: Date) => {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(
                            2,
                            '0'
                          );
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(
                            2,
                            '0'
                          );
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            '0'
                          );
                          return `${year}年${month}月${day}日 ${hours}:${minutes}`;
                        };

                        return (
                          <div
                            key={period.id}
                            className={`p-2 rounded-lg border text-sm ${
                              period.isCurrent
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            {/* 一段目: 氏名 (役職) (選択中の場合「現在」) */}
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {period.contactPersonName} (
                                {period.contactPersonPosition})
                              </span>
                              {period.isCurrent && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">
                                  現在
                                </span>
                              )}
                            </div>
                            {/* 二段目: 開始・終了日時（横に並べる） */}
                            <div className="text-xs text-gray-600 flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span>
                                  開始: {formatDateTime(period.startTime)}
                                </span>
                              </div>
                              {period.endTime && (
                                <div className="flex items-center gap-1">
                                  <svg
                                    className="h-3 w-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  <span>
                                    終了: {formatDateTime(period.endTime)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* 担当者情報 */}
                {selectedContactPerson && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <svg
                        className="h-5 w-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <h2 className="text-lg font-semibold text-gray-900">
                        担当者情報
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <svg
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-gray-500">担当者名</p>
                          <p className="font-medium text-gray-900">
                            {selectedContactPerson.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <svg
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-gray-500">役職</p>
                          <p className="font-medium text-gray-900">
                            {selectedContactPerson.position}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <svg
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-gray-500">
                            メールアドレス
                          </p>
                          <p className="font-medium text-gray-900">
                            {selectedContactPerson.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <svg
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-gray-500">電話番号</p>
                          <p className="font-medium text-gray-900">
                            {selectedContactPerson.phone}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* データ表示タブ */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                        <button
                          onClick={() => setDataType('first')}
                          className={`flex-1 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                            dataType === 'first'
                              ? 'bg-white text-blue-700 shadow-sm'
                              : 'text-gray-700 hover:text-gray-900'
                          }`}
                        >
                          ボタン押下後
                        </button>
                        <button
                          onClick={() => setDataType('latest')}
                          className={`flex-1 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                            dataType === 'latest'
                              ? 'bg-white text-blue-700 shadow-sm'
                              : 'text-gray-700 hover:text-gray-900'
                          }`}
                        >
                          最終保存データ
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ダッシュボード部分（Legacyと同じ背景色 - 端から端まで） */}
          <div
            className="py-6"
            style={{
              background: 'linear-gradient(135deg, #6a6ed5ff 0%, #5a5fcf 100%)',
            }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* メイン指標カード */}
              <MetricsCards
                data={dashboardData}
                calculatedData={calculatedData}
              />

              {/* ダッシュボードコンテンツ */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                {/* 左側：設定パネル（読み取り専用） */}
                <div className="lg:col-span-1">
                  <SettingsPanel
                    data={dashboardData}
                    onInputChange={handleInputChange}
                    onCheckboxChange={handleCheckboxChange}
                    isAnalysisEnabled={false}
                  />
                </div>

                {/* 右側：チャート */}
                <div className="lg:col-span-2">
                  <Charts
                    data={dashboardData}
                    calculatedData={calculatedData}
                  />
                </div>
              </div>

              {/* フッター */}
              <div className="border-t border-gray-200 pt-4 mt-6">
                <p className="text-xs text-white text-center">
                  © 2025 採用可能母集団試算システム -
                  データソース：厚生労働省・文部科学省統計データ
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default AdminDashboardDetailPage;
